import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  Facebook,
  Instagram,
  Check,
  ArrowRight,
  Plus,
  Gauge,
  ListChecks,
  MapPin,
  FileText,
  Link2,
  BarChart3,
  Utensils,
  Scale,
  Home,
  Stethoscope,
  ShoppingCart,
  Wrench,
  Search as SearchIcon,
  Target as TargetIcon,
  TrendingUp as TrendingUpIcon,
} from "lucide-react";
import { FOOTER_SOCIAL_LINKS } from "@/components/ui/SocialLinks";
import { HeroNav } from "@/components/ui/hero/HeroNav";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { HeroSocialProof } from "@/components/ui/hero/HeroSocialProof";
import { Footer } from "@/components/ui/footer";
import { SeoContactSection } from "@/components/sections/SeoContactSection";
import { AboutSection } from "@/components/sections/AboutSection";
import { GlowCard } from "@/components/ui/spotlight-card";
import { ShineBorder } from "@/components/ui/shine-border";

import { cn } from "@/lib/utils";
import { scrollToAnchor } from "@/lib/scroll-to-anchor";
import logo from "@/assets/mind-media-logo.svg";
import { ShareloEmbed } from "@/components/ui/ShareloEmbed";

export const Route = createFileRoute("/seo")({
  head: () => ({
    meta: [
      { name: "robots", content: "noindex, nofollow" },
      { title: "SEO Services — Mind Media" },
      {
        name: "description",
        content:
          "Design-led SEO from a senior team. We rank your business for the terms that bring in real work, with plain-English reporting and no long contracts.",
      },
      { property: "og:title", content: "SEO Services — Mind Media" },
      {
        property: "og:description",
        content:
          "Design-led SEO that gets your business found on Google. Senior team, no jargon, no lock-in.",
      },
      { property: "og:url", content: "https://mindmedia.co/seo" },
    ],
    links: [{ rel: "canonical", href: "https://mindmedia.co/seo" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          name: "SEO Services",
          serviceType: "Search Engine Optimization",
          provider: { "@type": "Organization", name: "Mind Media", url: "https://mindmedia.co" },
          areaServed: "Worldwide",
          url: "https://mindmedia.co/seo",
          description:
            "Design-led SEO from a senior team. We rank your business for the terms that bring in real work, with plain-English reporting and no long contracts.",
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            [
              "How long until I see results?",
              "Most clients see meaningful movement in rankings and traffic within the first 90 days, with bigger gains as the work compounds over six to twelve months. SEO builds equity, it doesn't flip a switch.",
            ],
            [
              "Do you guarantee a #1 ranking?",
              "No, and anyone who does is either lying or about to use tactics that get you penalized. Nobody controls Google's algorithm. We guarantee real work, honest reporting, and a strategy built to win over time.",
            ],
            [
              "Are there long-term contracts?",
              "No long lock-ins. SEO needs a few months to show its work, so we ask for a fair runway, but you stay because it's working, not because you're trapped.",
            ],
            [
              "What do I get each month?",
              "The work itself plus a plain-English report showing what we did, what moved, and what's next. No dashboards to decode, no vague updates.",
            ],
            [
              "What if I stop working with you?",
              "You keep everything. Every account, every piece of content, every bit of progress is yours. We don't hold your own assets hostage.",
            ],
            [
              "Why design-led SEO?",
              "Ranking is only half the job. If the traffic lands on a page that doesn't convert, you've paid for visitors, not customers. We handle both, so the clicks turn into work.",
            ],
          ].map(([q, a]) => ({
            "@type": "Question",
            name: q,
            acceptedAnswer: { "@type": "Answer", text: a },
          })),
        }),
      },
    ],
  }),
  component: SeoPage,
});

const gradText =
  "bg-gradient-to-r from-brand-blue via-purple-500 to-pink-500 bg-clip-text text-transparent";

function SeoPage() {
  const navigate = useNavigate();
  const goToMockup = () => navigate({ to: "/get-mockup" });

  return (
    <main className="min-h-screen bg-background text-foreground">
      {/* HERO */}
      <section className="relative isolate overflow-hidden bg-background pb-10 text-center sm:pb-14">
        <div
          aria-hidden
          className="pointer-events-none absolute -left-40 -bottom-40 sm:-left-56 sm:-bottom-56 h-[420px] w-[420px] sm:h-[720px] sm:w-[720px] rounded-full bg-hero-glow-warm opacity-35 blur-3xl sm:blur-[120px]"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -right-40 -top-40 sm:-right-56 sm:-top-56 h-[420px] w-[420px] sm:h-[720px] sm:w-[720px] rounded-full bg-hero-glow-cool opacity-40 blur-3xl sm:blur-[120px]"
        />
        <HeroNav
          logo={<img src={logo} alt="Mind Media" className="h-7 w-auto" />}
          navigation={[
            { label: "Work", href: "/#work" },
            { label: "About", href: "#about" },
            { label: "Services", href: "/#pricing" },
            { label: "Pricing", href: "#pricing" },
            { label: "FAQ", href: "#faq" },
            { label: "Contact", href: "#contact" },
          ]}
          ctaButton={{ label: "Book a Meeting", href: "#contact" }}
          mobilePricingHref="#pricing"
        />
        <div className="relative mx-auto px-6 mt-12 max-w-6xl -translate-y-5 text-left sm:text-center sm:mt-16">
          <HeroSocialProof mobileAlign="left" />
          <h1 className="mx-auto max-w-3xl text-4xl font-bold tracking-tight text-foreground sm:text-5xl lg:text-5xl">
            The customers are searching.
            <br />
            They're finding <span className={gradText}>someone else.</span>
          </h1>
          <p className="mx-auto mt-5 max-w-5xl text-lg text-muted-foreground sm:text-xl">
            <span className="md:block">
              We get your business ranking for the terms that actually bring in work, then show you
              in plain English
            </span>{" "}
            <span className="md:block">
              exactly what moved and why. Design-led SEO from a senior team, not a junior running a
              checklist.
            </span>
          </p>
          <div className="mt-8 flex flex-wrap justify-start gap-3 sm:justify-center">
            <a
              href="#contact"
              className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-brand-blue to-[#ec4899] px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-brand-blue/40 transition hover:-translate-y-0.5 hover:opacity-90"
            >
              Get a Free SEO Audit <ArrowRight className="h-4 w-4" />
            </a>
            <a
              href="#pricing"
              onClick={(e) => {
                e.preventDefault();
                scrollToAnchor("pricing");
              }}
              className="inline-flex items-center gap-2 rounded-full border border-border bg-background px-6 py-3 text-sm font-semibold text-foreground transition hover:-translate-y-0.5 hover:bg-accent"
            >
              Pricing
            </a>
          </div>
          <div className="relative mx-auto mt-8 flex flex-wrap justify-start gap-x-16 sm:justify-center gap-y-4">
            {["No long-term contracts", "White-hat only", "You own everything"].map((p) => (
              <span key={p} className="inline-flex items-center gap-2 text-base font-medium">
                <span className="flex h-5 w-5 items-center justify-center rounded-full border border-brand-green bg-white">
                  <Check className="h-3 w-3 text-brand-green" strokeWidth={3} />
                </span>
                {p}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* TRUST / STATS */}
      <section className="border-y border-border bg-muted/50 px-6 py-12">
        <div className="mx-auto max-w-5xl">
          <div className="grid grid-cols-2 gap-x-8 gap-y-8 md:grid-cols-4 md:gap-x-12 lg:gap-x-24 xl:gap-x-32">
            {[
              { num: "+187%", lbl: "Avg. traffic lift" },
              { num: "50+", lbl: "Businesses helped" },
              { num: "90d", lbl: "To real movement" },
              { num: "20+", lbl: "Years of experience" },
            ].map((s) => (
              <div key={s.lbl} className="min-w-[7.5rem] text-center lg:min-w-[8.75rem]">
                <div
                  className={cn(
                    "inline-block whitespace-nowrap text-4xl tracking-normal text-brand-blue md:text-5xl font-bold",
                  )}
                >
                  {s.num}
                </div>
                <div className="mt-1 text-sm font-semibold text-muted-foreground">{s.lbl}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* VIDEO + WHAT GREAT SEO DOES */}
      <section className="px-6 pt-10 pb-12 lg:pt-14 lg:pb-16">
        <div className="mx-auto max-w-7xl lg:px-4">
          <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-16">
            <ShareloEmbed id="NpZj66JQDA2KjpmfapSS1" title="SEO — customer story" />

            <div className="flex flex-col gap-8">
              <ShineBorder
                borderRadius={16}
                borderWidth={2}
                duration={10}
                color={["#3b82f6", "#a855f7", "#ec4899"]}
                className="w-full max-w-[32rem]"
              >
                <GlowCard
                  glowColor="blue"
                  disableInnerGlow
                  customSize
                  className="relative w-full bg-card"
                >
                  <div className="py-4 px-6 sm:py-5 sm:px-8">
                    <h3 className="text-center text-[1.7rem] font-bold tracking-tight text-foreground sm:text-[1.7rem]">
                      <span className="block sm:inline">What great</span>{" "}
                      <span className="block sm:inline">
                        <span className="inline-flex whitespace-nowrap sm:inline">
                          SEO
                          <span className="inline sm:hidden">{"\u00A0"}</span>
                          <span className="hidden sm:inline"> </span>
                          <span className="text-gradient-cycle">actually does</span>
                        </span>
                      </span>
                    </h3>
                    <ul className="mt-2">
                      {[
                        {
                          title: "Get found on Google",
                          description:
                            "Rank for the terms your customers are already searching for.",
                          Icon: SearchIcon,
                          bgClass: "bg-[#3b82f6]/10",
                          textClass: "text-[#3b82f6]",
                        },
                        {
                          title: "Bring in the right traffic",
                          description: "Not just clicks. Buyers, callers, and booked jobs.",
                          Icon: TargetIcon,
                          bgClass: "bg-[#a855f7]/10",
                          textClass: "text-[#a855f7]",
                        },
                        {
                          title: "Compound over time",
                          description:
                            "Traffic that keeps growing, long after the ad budget stops.",
                          Icon: TrendingUpIcon,
                          bgClass: "bg-[#ec4899]/10",
                          textClass: "text-[#ec4899]",
                        },
                      ].map(({ title, description, Icon, bgClass, textClass }, i) => (
                        <li key={title}>
                          {i > 0 && (
                            <div className="h-px w-full bg-gradient-to-r from-transparent via-foreground/15 to-transparent" />
                          )}
                          <div className="flex items-start gap-4 py-5">
                            <span
                              className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full ${bgClass}`}
                            >
                              <Icon className={`h-6 w-6 ${textClass}`} strokeWidth={2.25} />
                            </span>
                            <div className="text-left">
                              <p className="text-lg text-foreground font-semibold">{title}</p>
                              <p className="text-sm leading-relaxed text-muted-foreground">
                                {description}
                              </p>
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                </GlowCard>
              </ShineBorder>
            </div>
          </div>
        </div>
      </section>

      {/* PROBLEM */}
      <section className="bg-muted/30 px-6 py-20">
        <div className="mx-auto max-w-7xl">
          <SectionHead
            kickerWeight="font-bold"
            kicker="Why most sites stay invisible"
            title={
              <>
                A beautiful site nobody finds is just an{" "}
                <span className={gradText}>expensive brochure.</span>
              </>
            }
          >
            Most businesses pour money into a website, then wonder why the phone stays quiet. The
            traffic is out there. It's going to whoever shows up first.
          </SectionHead>
          <div className="grid gap-6 md:grid-cols-3">
            {[
              {
                n: "93%",
                h: "Searches start on Google",
                p: "If you're not on page one for what you sell, you're invisible to almost everyone looking right now.",
              },
              {
                n: "75%",
                h: "Never scroll past page one",
                p: "Ranking tenth isn't a little behind. For the people who matter, it's not ranking at all.",
              },
              {
                n: "0",
                h: "Reports you can read",
                p: "Most agencies hide behind jargon. You should know exactly what you're paying for every month.",
              },
            ].map((c) => (
              <div key={c.h} className="rounded-2xl border border-border bg-card p-7 shadow-sm">
                <div className="flex items-center gap-3">
                  <div
                    className={cn("text-4xl tracking-tight md:text-5xl font-bold text-brand-blue")}
                  >
                    {c.n}
                  </div>
                  <h3 className="min-w-0 whitespace-nowrap text-[15px] font-bold leading-tight tracking-tight lg:text-[16px] xl:text-lg">
                    {c.h}
                  </h3>
                </div>
                <p className="mt-3 text-[15px] text-muted-foreground">{c.p}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" className="px-6 py-20">
        <div className="mx-auto max-w-6xl">
          <SectionHead
            maxWidth="max-w-4xl"
            kickerWeight="font-bold"
            kicker="What's included"
            title={
              <>
                Everything that moves rankings, <span className={gradText}>handled.</span>
              </>
            }
          >
            SEO isn't one thing. It's a stack of work done consistently and done right. Here's what
            we cover.
          </SectionHead>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {[
              {
                icon: Gauge,
                h: "Technical SEO",
                p: "Fix the behind-the-scenes issues quietly killing your rankings before we build on top.",
                b: [
                  "Site speed & Core Web Vitals",
                  "Crawlability & indexing",
                  "Mobile & schema markup",
                ],
              },
              {
                icon: ListChecks,
                h: "On-Page SEO",
                p: "Make every page earn its ranking with the structure and targeting Google rewards.",
                b: [
                  "Keyword research & mapping",
                  "Titles, meta & headings",
                  "Internal linking strategy",
                ],
              },
              {
                icon: MapPin,
                h: "Local SEO",
                p: "Own the map pack so nearby customers find you first, not the competitor across town.",
                b: ["Google Business Profile", "Local citations & directories", "Review strategy"],
              },
              {
                icon: FileText,
                h: "Content Strategy",
                p: "Rank for the questions your customers actually type, and become the obvious answer.",
                b: [
                  "Search-intent content planning",
                  "Landing page optimization",
                  "Refresh of existing content",
                ],
              },
              {
                icon: Link2,
                h: "Link Building",
                p: "Earn the authority that tells Google you're credible, the safe way that won't get you penalized.",
                b: ["White-hat outreach", "Digital PR", "Toxic backlink cleanup"],
              },
              {
                icon: BarChart3,
                h: "Reporting & Analytics",
                p: "Know exactly what you're paying for. Real numbers, explained in plain English, every month.",
                b: [
                  "GA4 & Search Console setup",
                  "Monthly plain-English reports",
                  "Ranking & traffic tracking",
                ],
              },
            ].map(({ icon: Icon, h, p, b }) => (
              <div
                key={h}
                className="group rounded-2xl border border-border bg-card p-7 transition hover:-translate-y-1 hover:border-brand-blue/30 hover:shadow-xl hover:shadow-brand-blue/10"
              >
                <div className="mb-4 flex items-center gap-3">
                  <div className="inline-flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-brand-blue/15 to-pink-500/15">
                    <Icon className="h-5 w-5 text-brand-blue" strokeWidth={2.25} />
                  </div>
                  <h3 className="text-lg font-bold tracking-tight">{h}</h3>
                </div>
                <p className="text-[15px] text-muted-foreground">{p}</p>
                <ul className="mt-4 space-y-2">
                  {b.map((li) => (
                    <li key={li} className="flex items-start gap-2 text-sm font-medium">
                      <Check
                        className="mt-0.5 h-4 w-4 flex-shrink-0 text-brand-green"
                        strokeWidth={3}
                      />
                      {li}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section id="process" className="bg-muted/30 px-6 py-20">
        <div className="mx-auto max-w-6xl">
          <SectionHead
            maxWidth="max-w-4xl"
            kickerWeight="font-bold"
            kicker="How it works"
            title={
              <>
                A clear process. <span className={`${gradText} block sm:inline`}>No mystery.</span>
              </>
            }
          >
            You'll always know what we did, what changed, and what's next. Here's the order of
            operations.
          </SectionHead>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {[
              {
                n: 1,
                h: "Audit",
                p: "We dig into your site, competitors, and the keywords worth chasing to find the fastest wins.",
              },
              {
                n: 2,
                h: "Fix the foundation",
                p: "Technical issues, speed, and on-page basics first, so everything after it compounds.",
              },
              {
                n: 3,
                h: "Build authority",
                p: "Content, local signals, and quality links go to work raising your rankings over time.",
              },
              {
                n: 4,
                h: "Report & adjust",
                p: "Every month you get a clear read on what moved, and we double down on what works.",
              },
            ].map((s) => (
              <div
                key={s.n}
                className="flex items-center gap-5 rounded-2xl border border-border bg-card p-6"
              >
                <div className="flex-shrink-0 text-6xl leading-none tracking-tight font-bold text-brand-blue">
                  {s.n}
                </div>
                <div>
                  <h3 className="text-base font-bold tracking-tight">{s.h}</h3>
                  <p className="mt-2 text-sm text-muted-foreground">{s.p}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="px-6 pt-20 pb-12 sm:py-20">
        <div className="mx-auto max-w-6xl">
          <div className="mx-auto max-w-5xl text-center">
            <Kicker className="font-bold">Why work with us</Kicker>
            <h2 className="mt-4 text-4xl font-bold tracking-tight text-foreground sm:text-5xl lg:text-5xl lg:whitespace-nowrap">
              Senior, design-led SEO. <span className={gradText}>No runaround.</span>
            </h2>
            <p className="mx-auto mt-4 max-w-4xl text-lg text-muted-foreground">
              You're not handed to a junior on a template. You work with a team that's built and
              grown brands for fifteen years, and the same people who rank you also make the site
              convert once the traffic shows up.
            </p>
          </div>
          <div className="mt-12 grid items-center gap-12 lg:grid-cols-2">
            <ul className="space-y-5">
              {[
                [
                  "Plain-English reporting",
                  "No jargon dumps. You'll understand exactly what we did and why it matters.",
                ],
                [
                  "No long lock-in contracts",
                  "We earn the next month by delivering this one. Stay because it works.",
                ],
                [
                  "White-hat, always",
                  "No shortcuts that spike for a month then get you penalized by Google.",
                ],
                [
                  "You own everything",
                  "Every account, asset, and bit of progress is yours, even if we part ways.",
                ],
              ].map(([title, body]) => (
                <li key={title} className="flex gap-4">
                  <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-brand-blue/15 to-pink-500/15">
                    <Check className="h-4 w-4 text-brand-blue" strokeWidth={3} />
                  </span>
                  <div>
                    <strong className="block text-base font-bold">{title}</strong>
                    <span className="text-[15px] text-muted-foreground">{body}</span>
                  </div>
                </li>
              ))}
            </ul>

            <div className="relative mt-16 rounded-3xl bg-gradient-to-br from-brand-blue via-purple-600 to-pink-500 p-10 text-white shadow-2xl shadow-purple-500/30 sm:mt-0">
              <div className="absolute right-6 top-6 text-right">
                <div className="text-3xl font-bold leading-none">20+</div>
                <div className="text-xs font-medium uppercase tracking-wider text-white/80">
                  YEARS EXP
                </div>
              </div>
              <div className="text-6xl tracking-tight leading-none font-bold text-white">
                90 days
              </div>
              <div className="mt-2 text-lg font-bold">to meaningful movement</div>
              <p className="mt-5 text-[15px] leading-relaxed text-white/90">
                SEO is a compounding game, not a switch. Most clients see real ranking and traffic
                gains within the first quarter, and it keeps building from there. Anyone promising
                page one next week is lying to you.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* WHO WE HELP */}
      <section id="who-we-help" className="px-6 py-20">
        <div className="mx-auto max-w-6xl">
          <SectionHead
            maxWidth="max-w-4xl"
            kickerWeight="font-bold"
            kicker="Who we help"
            title={
              <>
                The businesses we get <span className={gradText}>found on Google.</span>
              </>
            }
          >
            SEO isn't one-size-fits-all. Here's what ranking first looks like across the industries
            we work with most.
          </SectionHead>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {[
              {
                icon: Utensils,
                h: "Restaurants",
                p: 'Show up first when nearby diners search "restaurants near me," with your menu, hours, and reviews front and center.',
              },
              {
                icon: Scale,
                h: "Law Firms",
                p: "Rank for the high-value local searches that bring in real cases, where a single client can pay for months of work.",
              },
              {
                icon: Home,
                h: "Real Estate",
                p: "Own the neighborhood and listing searches so buyers and sellers find you before they land on the big portals.",
              },
              {
                icon: Stethoscope,
                h: "Medical Offices",
                p: "Get found by new patients searching for your services nearby, backed by the reviews that make them choose you.",
              },
              {
                icon: ShoppingCart,
                h: "E-Commerce",
                p: "Rank your product and category pages so you pull in steady organic sales instead of paying for every single click.",
              },
              {
                icon: Wrench,
                h: "Service Businesses",
                p: "Be the first name that shows up when someone needs a contractor, cleaner, or repair right now and is ready to book.",
              },
            ].map(({ icon: Icon, h, p }) => (
              <div
                key={h}
                className="group rounded-2xl border border-border bg-card p-7 transition hover:-translate-y-1 hover:border-brand-blue/30 hover:shadow-xl hover:shadow-brand-blue/10"
              >
                <div className="mb-4 flex items-center gap-3">
                  <div className="inline-flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-brand-blue/15 to-pink-500/15">
                    <Icon className="h-5 w-5 text-brand-blue" strokeWidth={2.25} />
                  </div>
                  <h3 className="text-lg font-bold tracking-tight">{h}</h3>
                </div>
                <p className="text-[15px] text-muted-foreground">{p}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <AboutSection
        className="pt-2 pb-6 sm:pt-4 sm:pb-8"
        contentClassName="mt-2 sm:mt-4"
        hideKicker
        hideMockupButton
        meetingButtonPrimary
        centerContent
      />

      {/* PRICING */}
      <section id="pricing" className="px-6 py-20">
        <div className="mx-auto max-w-6xl">
          <SectionHead
            kickerWeight="font-bold"
            kicker="Simple pricing"
            maxWidth="max-w-5xl"
            titleClassName="lg:whitespace-nowrap"
            title={
              <>
                Pick a plan that fits <span className={gradText}>where you are.</span>
              </>
            }
          >
            Monthly retainers, no surprise fees. Cancel anytime once we've earned our keep.
          </SectionHead>
          <div className="grid items-stretch gap-6 md:grid-cols-3">
            {[
              {
                h: "Local",
                desc: "For small and local businesses",
                amt: "$1,500",
                sub: "Best for getting found in your area",
                b: [
                  "Technical & on-page SEO",
                  "Google Business Profile",
                  "Local citations",
                  "Monthly report",
                ],
                cta: "blue",
                feat: false,
              },
              {
                h: "Growth",
                desc: "For businesses ready to scale",
                amt: "$3,500",
                sub: "Best for steady, compounding growth",
                b: [
                  "Everything in Local",
                  "Content strategy & creation",
                  "Link building",
                  "Weekly reporting",
                ],
                cta: "primary",
                feat: true,
              },
              {
                h: "Custom",
                desc: "For larger or competitive markets",
                amt: "Let's talk",
                sub: "Scoped to your goals and competition",
                b: [
                  "Everything in Growth",
                  "Aggressive content volume",
                  "Digital PR campaigns",
                  "Dedicated strategy calls",
                ],
                cta: "blue",
                feat: false,
              },
            ].map((p) => (
              <div
                key={p.h}
                className={cn(
                  "relative flex flex-col rounded-2xl border bg-card p-8",
                  p.feat
                    ? "border-transparent shadow-2xl shadow-purple-500/20 ring-2 ring-transparent [background:linear-gradient(var(--card),var(--card))_padding-box,linear-gradient(100deg,oklch(0.585_0.196_263),#a855f7,#ec4899)_border-box]"
                    : "border-border",
                )}
              >
                {p.feat && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-gradient-to-r from-brand-blue via-purple-500 to-pink-500 px-4 py-1.5 text-[11px] font-extrabold uppercase tracking-wider text-white">
                    Most popular
                  </span>
                )}
                <h3 className="text-lg font-bold tracking-tight">{p.h}</h3>
                <div className="mt-1 text-sm text-muted-foreground">{p.desc}</div>
                <div className="mt-5 text-4xl font-extrabold tracking-tight">
                  {p.amt}
                  {p.amt.startsWith("$") && (
                    <small className="ml-1 text-sm font-semibold text-muted-foreground">/mo</small>
                  )}
                </div>
                <div className="mt-1 text-xs font-medium text-muted-foreground">{p.sub}</div>
                <ul className="mt-6 flex-1 space-y-3">
                  {p.b.map((li) => (
                    <li key={li} className="flex items-start gap-2 text-sm font-medium">
                      <Check
                        className="mt-0.5 h-4 w-4 flex-shrink-0 text-brand-green"
                        strokeWidth={3}
                      />
                      {li}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={goToMockup}
                  className={cn(
                    "mt-6 inline-flex w-full items-center justify-center rounded-full px-5 py-3 text-sm font-bold transition hover:-translate-y-0.5",
                    p.cta === "primary"
                      ? "bg-gradient-to-r from-brand-blue via-purple-500 to-pink-500 text-white shadow-lg shadow-purple-500/40 hover:opacity-90"
                      : p.cta === "blue"
                        ? "border-2 border-brand-blue bg-background text-brand-blue hover:bg-brand-blue/10"
                        : "border border-border bg-background text-foreground hover:bg-accent",
                  )}
                >
                  {p.amt === "Let's talk" ? "Book a call" : "Get started"}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="bg-muted/30 px-6 py-20">
        <div className="mx-auto max-w-5xl">
          <SectionHead
            kickerWeight="font-bold"
            kicker="Questions"
            title={
              <>
                The stuff people <span className={gradText}>actually ask.</span>
              </>
            }
          />
          {(() => {
            const items: [string, string][] = [
              [
                "How long until I see results?",
                "Most clients see meaningful movement in rankings and traffic within the first 90 days, with bigger gains as the work compounds over six to twelve months. SEO builds equity, it doesn't flip a switch.",
              ],
              [
                "Do you guarantee a #1 ranking?",
                "No, and anyone who does is either lying or about to use tactics that get you penalized. Nobody controls Google's algorithm. We guarantee real work, honest reporting, and a strategy built to win over time.",
              ],
              [
                "Are there long-term contracts?",
                "No long lock-ins. SEO needs a few months to show its work, so we ask for a fair runway, but you stay because it's working, not because you're trapped.",
              ],
              [
                "What do I get each month?",
                "The work itself plus a plain-English report showing what we did, what moved, and what's next. No dashboards to decode, no vague updates.",
              ],
              [
                "What if I stop working with you?",
                "You keep everything. Every account, every piece of content, every bit of progress is yours. We don't hold your own assets hostage.",
              ],
              [
                "Why design-led SEO?",
                "Ranking is only half the job. If the traffic lands on a page that doesn't convert, you've paid for visitors, not customers. We handle both, so the clicks turn into work.",
              ],
            ];
            const col1 = items.filter((_, i) => i % 2 === 0);
            const col2 = items.filter((_, i) => i % 2 === 1);
            return (
              <div className="mx-auto mt-12 grid max-w-6xl gap-x-12 gap-y-2 md:grid-cols-2">
                <FaqColumn items={col1} />
                <FaqColumn items={col2} />
              </div>
            );
          })()}
        </div>
      </section>

      <SeoContactSection compact />

      <Footer
        logo={<img src={logo} alt="Mind Media" className="h-7 w-auto" />}
        brandName="Mind Media"
        socialLinks={FOOTER_SOCIAL_LINKS}
        mainLinks={[
          { href: "/", label: "Home" },
          { href: "/seo", label: "SEO" },
          { href: "/#pricing", label: "Pricing" },
          { href: "#faq", label: "FAQ" },
          { href: "/blog", label: "Blog" },
          { href: "/services", label: "Services" },
        ]}
        legalLinks={[
          { href: "/privacy", label: "Privacy" },
          { href: "/terms", label: "Terms" },
        ]}
        copyright={{
          text: `© ${new Date().getFullYear()} Mind Media`,
          license: "All rights reserved",
        }}
      />
    </main>
  );
}

function Kicker({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <span
      className={cn(
        "inline-block rounded-full bg-brand-blue/10 px-4 py-1.5 text-xs uppercase tracking-[0.12em] text-brand-blue",
        className,
      )}
    >
      {children}
    </span>
  );
}

function SectionHead({
  kicker,
  title,
  children,
  kickerWeight = "font-bold",
  maxWidth = "max-w-2xl",
  titleClassName,
}: {
  kicker: string;
  title: React.ReactNode;
  children?: React.ReactNode;
  kickerWeight?: "font-bold" | "font-medium";
  maxWidth?: string;
  titleClassName?: string;
}) {
  return (
    <div className={cn("mx-auto mb-12 text-center", maxWidth)}>
      <Kicker className={kickerWeight}>{kicker}</Kicker>
      <h2
        className={cn(
          "mt-4 text-4xl font-bold tracking-tight text-foreground sm:text-5xl lg:text-5xl",
          titleClassName,
        )}
      >
        {title}
      </h2>
      {children && <p className="mt-4 text-lg leading-relaxed text-muted-foreground">{children}</p>}
    </div>
  );
}

function FaqColumn({ items }: { items: [string, string][] }) {
  return (
    <Accordion type="single" collapsible className="w-full">
      {items.map(([q, a]) => (
        <AccordionItem key={q} value={q} className="border-b">
          <AccordionTrigger className="text-left text-base font-medium text-gray-950 md:text-lg">
            {q}
          </AccordionTrigger>
          <AccordionContent>
            <p className="text-muted-foreground text-base">{a}</p>
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
}
